/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * Token Cache Service
 *
 * Caches authentication tokens to reduce network overhead.
 * Implements automatic refresh before expiry.
 *
 * Features:
 * - Token caching with automatic expiry
 * - Proactive refresh before token expires
 * - Exponential backoff retry
 * - Memory leak prevention with proper timer cleanup
 * - Visibility-aware refresh (pause when tab hidden)
 */

import * as Sentry from "@sentry/react";

import { authServiceBaseUrl } from "@/lib/auth-redirect";
import { logger } from "@/lib/logger";

interface TokenCacheStats {
  cacheMisses: number;
  refreshCount: number;
  failedRefreshes: number;
  lastRefreshAt: number | null;
}

/**
 * N1 (FCR S687): the outcome of a renewal attempt, as three cases rather than
 * `string | null`.
 *
 * `refreshToken(): Promise<string | null>` conflated "you are signed out" with
 * "try again later", and no caller could make the right decision from that
 * value. Both 401 handlers destroyed the customer's tokens and redirected for
 * EVERY unsuccessful outcome -- a rejected refresh token, a service outage, a
 * network failure, a rate limit, and the thirty-second client throttle. The
 * throttle case is the sharpest: a renewal fails with a 503, a second request
 * returns 401 within thirty seconds, the throttle branch answers null because
 * the access token has expired, and a perfectly good thirty-day refresh token is
 * destroyed by a rate limiter that was added to protect the service.
 */
export type RenewalOutcome =
  | { status: "renewed"; token: string }
  /** 400 / 401 / 403 -- the credential is no good. */
  | { status: "signed-out" }
  /** Everything else -- keep the tokens and try later. */
  | { status: "unavailable" };

/**
 * N8 (FCR S687): report the SYSTEMATIC renewal failures to monitoring, and only
 * those.
 *
 * logger.warn never reaches Sentry -- logger.ts forwards only errors carrying an
 * Error instance -- so every interesting renewal failure was a warning that went
 * nowhere. The failure that matters most is every customer's renewal starting to
 * fail at once: the audience changed, CORS was tightened, VITE_AUTH_SERVICE_URL
 * was unset in a deployment, or the refresh route moved. That would have
 * produced nothing in monitoring and been found by a customer, which is the
 * class of silent failure this shop has already met twice.
 *
 * Deliberately narrow. An ordinary 401 (a thirty-day token that has simply
 * expired), a 429 and a network error are NOT reported per occurrence -- that is
 * the noisy-duplicate failure that makes monitoring useless.
 */
function reportRenewalFault(message: string, context: Record<string, unknown> = {}): void {
  try {
    Sentry.captureMessage(message, {
      level: "error",
      extra: context,
    });
  } catch {
    // Monitoring must never break the renewal path.
  }
}

/** N8: the configuration fault is reported once per page load, not per call. */
let missingAuthServiceUrlReported = false;

class TokenCacheService {
  private refreshPromise: Promise<RenewalOutcome> | null = null;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private visibilityHandler: (() => void) | null = null;
  private isPageVisible = true;
  /** Requirement 7 (FCR S674): a renewal is in flight; the guards must hold. */
  private renewalPending = false;
  /**
   * Has a renewal been attempted since this page loaded?
   *
   * This is what BOUNDS the hold. "There is a refresh token, so wait" with
   * nothing to end it is a spinner that never resolves -- and correction 2 says
   * a server error must leave both tokens in place, so the refresh token being
   * present is not evidence that renewing it will ever succeed. The guards hold
   * until one attempt has been made and no longer.
   */
  private renewalAttempted = false;
  /**
   * N7 (FCR S687): a proactive renewal fell due while the tab was hidden.
   *
   * The proactive timer used to `return` when the page was not visible, with no
   * else, no reschedule and no retry -- so the renewal for that hour simply did
   * not happen. It is now marked owed, and the visibility handler picks it up
   * when the customer comes back.
   *
   * Deferring rather than renewing in the background is deliberate: browsers
   * throttle timers in hidden tabs to once a minute or less, and a hidden tab
   * renewing on its own spends the service's per-address budget for a page
   * nobody is looking at.
   */
  private renewalOwed = false;

  private readonly TOKEN_REFRESH_BUFFER = 60 * 1000; // Refresh 1 minute before expiry
  // Session 545 decision (confirmed by Sunil): 60 minutes for SaaS dashboard.
  // Pre-cutover: used as fallback when Better Auth token endpoint omits expiresIn.
  // Post-cutover: the ds_auth_token exp claim is the authoritative source (read by
  // getStoredAuthToken); this constant is no longer in the critical path.
  private readonly DEFAULT_TOKEN_TTL_MS = 60 * 60 * 1000; // 60 minutes
  private readonly MIN_REFRESH_INTERVAL = 30 * 1000; // Don't refresh more than once per 30s

  private stats: TokenCacheStats = {
    cacheMisses: 0,
    refreshCount: 0,
    failedRefreshes: 0,
    lastRefreshAt: null,
  };

  constructor() {
    // Set up visibility change handler to pause/resume refresh
    if (typeof document !== "undefined") {
      this.visibilityHandler = () => {
        this.isPageVisible = document.visibilityState === "visible";
        if (!this.isPageVisible) return;
        /*
         * N7 (FCR S687): ask the RIGHT question.
         *
         * This used to read `if (this.isPageVisible && this.cachedToken)`, and
         * `this.cachedToken` was the in-memory cache from the retired network
         * path -- onTokensStored() set it to null on every store and nothing on
         * the renewal path ever filled it, so it was permanently null and this
         * body could never run. The one mechanism meant to catch a missed
         * renewal could not fire, and a reader auditing the handler saw a
         * plausible guard and moved on. That is what dead code costs, and it is
         * why A10 removes the cache entirely.
         *
         * Renew when one is owed, or when the stored access token is gone or
         * spent while a refresh token remains.
         */
        if (this.renewalOwed || (!getStoredAuthToken() && getStoredRefreshToken())) {
          this.renewalOwed = false;
          void this.refreshToken().catch(() => {
            // Handled by the refresh path; the next API call will retry.
          });
        }
      };
      document.addEventListener("visibilitychange", this.visibilityHandler);
    }
  }

  /**
   * Get a valid token, fetching a new one if necessary
   */
  async getToken(): Promise<string | null> {
    // Issue 2 (FCR Pass 10) Wave A+: ds_auth_token (the Auth Service JWT) is the
    // session. getStoredAuthToken() treats an expired token as absent, so a
    // spent access token falls through to the renewal below.
    const storedJwt = getStoredAuthToken();
    if (storedJwt) {
      logger.debug("Using stored Auth Service JWT from ds_auth_token");
      return storedJwt;
    }

    this.stats.cacheMisses++;

    /*
     * Correction 2 (FCR S687, item 4): NOT changed to add an earlier
     * "if there is a refresh token, call refreshToken()" branch. This already
     * ends in refreshToken(), and refreshToken() already deduplicates on
     * this.refreshPromise -- adding that branch would duplicate both behaviours
     * in a function that already has them. Now that the renewal makes a real
     * call, getToken() needs no edit at all.
     *
     * A10 (S687): the in-memory cache that used to sit here is gone. It could
     * never run -- onTokensStored() nulled it on every store and the renewal
     * path never filled it -- so isTokenValid() was always false, hasToken()
     * always false, getTimeUntilExpiry() always null and cacheHits always 0.
     */
    return this.refreshToken();
  }

  /**
   * A11 (FCR S687): why there are two functions rather than one.
   *
   * `renewSession()` is what you call when the ANSWER changes what you do next;
   * `refreshToken()` is what you call when you just want a renewal attempted.
   * Only the two 401 handlers need to know why a renewal failed, because they
   * are the only places that decide whether to sign the customer out. The other
   * callers -- the proactive timer, the visibility handler and
   * beginRenewalIfNeeded() -- only need to know that an attempt happened.
   * Unwrapping a result object in all of them would be a larger edit with no
   * behavioural gain, and would rewrite tests that are currently correct.
   */
  async refreshToken(): Promise<string | null> {
    const outcome = await this.renewSession();
    return outcome.status === "renewed" ? outcome.token : null;
  }

  /**
   * Renew the session and say what happened. See RenewalOutcome.
   */
  async renewSession(): Promise<RenewalOutcome> {
    // Requirement 7 (FCR S674): a renewal has now been asked for, and that is
    // what ends the guards' hold -- set before the two early returns below.
    //
    // Both of them ARE answers: an in-flight promise will settle, and the
    // throttle answers from storage. If the flag were set after them, a guard
    // holding for a renewal that the throttle answered would keep holding, and
    // the customer would watch a spinner instead of their dashboard.
    this.renewalAttempted = true;

    // Prevent concurrent refresh requests
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    // Throttle refreshes to prevent hammering the auth server.
    //
    // Correction 1 (FCR S674): the throttle branch answers STORAGE, not the
    // in-memory cache.
    //
    // It used to answer `this.cachedToken`, which the renewal path never fills
    // -- the renewed token is written to localStorage. So a second 401 within
    // thirty seconds of a successful refresh returned null, and the interceptor
    // sent the customer to sign in with a perfectly good token sitting in
    // storage. Two parallel requests failing together is the ordinary case on a
    // dashboard, not a rare one, so this was a sign-out waiting to happen on
    // every renewal.
    const now = Date.now();
    if (
      this.stats.lastRefreshAt &&
      now - this.stats.lastRefreshAt < this.MIN_REFRESH_INTERVAL
    ) {
      logger.debug("Throttling token refresh - too soon since last refresh");
      const stored = getStoredAuthToken();
      if (stored) return { status: "renewed", token: stored };
      /*
       * N1 (S687): the throttle answers `unavailable`, NOT `signed-out`. It is a
       * wait, not a verdict -- nothing here has been told the credential is bad.
       * This is the case that used to destroy a valid thirty-day refresh token
       * because a client-side rate limiter happened to be inside its window.
       */
      return { status: "unavailable" };
    }

    // Requirement 7: the guards hold while this is in flight.
    this.setRenewalPending(true);
    // Correction 1 (FCR S674): record the attempt, so the throttle above is a
    // throttle.
    //
    // `lastRefreshAt` was never written anywhere, so the branch that reads it
    // could not fire and the thirty-second rule did nothing at all. That made
    // correction 1 look academic; it is not. The moment a renewal path exists,
    // an unthrottled one hammers the service on every parallel 401 -- and a
    // throttle that answers a cache the renewal never fills signs the customer
    // out instead, which is the defect correction 1 names. Both halves have to
    // be right, so both are here.
    this.stats.refreshCount++;
    this.stats.lastRefreshAt = Date.now();
    this.refreshPromise = this.fetchTokenWithRetry();

    try {
      const outcome = await this.refreshPromise;
      if (outcome.status !== "renewed") this.stats.failedRefreshes++;
      return outcome;
    } finally {
      this.refreshPromise = null;
      this.setRenewalPending(false);
    }
  }

  /**
   * Item 4 (FCR S687): the silent access-token renewal -- the request itself.
   *
   * Everything around this already existed and achieved nothing, because this
   * function returned null without making any network call: shouldHoldForRenewal(),
   * beginRenewalIfNeeded(), shouldClearTokensForRefreshStatus(), storeAuthTokens(),
   * getStoredRefreshToken() and scheduleProactiveRefreshFromStorage() are all
   * correct and were all driving a renewal that never happened.
   *
   * N3 (S687) -- and this is the most important line here: NO
   * `credentials: "include"`. The authentication service reads the refresh token
   * from the `ds_refresh` cookie BEFORE the request body
   * (`_read_refresh_token()` is documented "Cookie first, request body second"),
   * that cookie is HttpOnly and scoped to the shared `.deskshare.com` parent
   * domain across all four token routes, and the shop does not own it. With
   * credentials sent, three things follow in increasing severity: the shop's own
   * refresh token is never used (its session is driven by a credential it cannot
   * read); signing out of the shop signs the customer out of the WEBSITE, because
   * the revoke route reads the cookie first and revokes the whole family; and the
   * renewed access token can carry the wrong AUDIENCE, because TokenRefreshView
   * mints the successor with `audience=row.consumer` -- after which this shop's
   * middleware rejects every call, the 401 handler signs the customer out, they
   * sign in again, and it repeats.
   *
   * That last one cannot be caught by testing: SameSite=Lax blocks the cookie on
   * a cross-SITE request, and a developer testing two local ports sees a setup
   * where the cookie behaves differently and concludes the concern is
   * theoretical. In staging and production the shop and the service sit under one
   * registrable domain, the request is same-site, and the cookie is sent.
   *
   * Not sending credentials also removes a deployment requirement: no
   * Access-Control-Allow-Credentials and no exact origin echo. The shop's origin
   * still has to be in the service's CORS_ALLOWED_ORIGINS -- CORS_ALLOW_ALL_ORIGINS
   * is False there -- but nothing more.
   */
  private async fetchTokenWithRetry(): Promise<RenewalOutcome> {
    const refresh = getStoredRefreshToken();
    if (!refresh) {
      logger.debug("No refresh token -- customer is not signed in");
      return { status: "signed-out" };
    }

    const baseUrl = authServiceBaseUrl();
    if (!baseUrl) {
      logger.warn("Auth Service URL not configured -- cannot renew the session");
      // N8: a configuration fault, reported ONCE per page load rather than per
      // call. In a deployed environment this means VITE_AUTH_SERVICE_URL is
      // unset and no customer can renew; it is exactly the systematic shape
      // that used to reach nobody.
      if (!missingAuthServiceUrlReported) {
        missingAuthServiceUrlReported = true;
        reportRenewalFault(
          "VITE_AUTH_SERVICE_URL is not configured -- no session can be renewed",
        );
      }
      // Not signed out: the customer's credentials are fine, the shop simply
      // cannot use them here.
      return { status: "unavailable" };
    }

    let status: number | null = null;
    try {
      const response = await fetch(`${baseUrl}/api/auth/token/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ refresh_token: refresh }),
        // No credentials. See the note on the ds_refresh cookie above (N3).
      });
      status = response.status;

      if (!response.ok) {
        logger.warn("Token refresh failed", { status });
        if (shouldClearTokensForRefreshStatus(status)) {
          /*
           * N6 (FCR S687): another tab may have renewed while this request was
           * in flight.
           *
           * The service rotates a refresh token on every use and judges a
           * re-presented one by how long ago it rotated: inside
           * JWT_REFRESH_ROTATION_LEEWAY_SECONDS (30) it treats it as two tabs
           * racing and issues a successor; OUTSIDE that window it revokes the
           * ENTIRE FAMILY and signs both holders out. Two shop tabs open past
           * the sixty-minute mark is ordinary behaviour on a dashboard and a
           * purchase-history page, and a request queued behind a slow network,
           * held by the thirty-second client throttle, or delayed by the browser
           * throttling a background tab can present the old value late.
           *
           * So before concluding anything: if the stored refresh token is no
           * longer the one we sent, this 401 is about a value that has already
           * been superseded, not about the session.
           *
           * This does not remove the race -- it removes the harm from the common
           * case. The remaining exposure is genuine simultaneity outside the
           * leeway, which the service's own thirty-second window exists to
           * absorb. Removing it entirely would mean a cross-tab lock, which is
           * not worth the complexity here. And do NOT "fix" this by asking the
           * authentication service to raise that leeway: it is a security
           * setting, its comment explains the trade-off deliberately, and it
           * belongs to another team.
           */
          if (getStoredRefreshToken() !== refresh) {
            logger.debug(
              "Another tab renewed the session while this request was in flight",
            );
            const current = getStoredAuthToken();
            return current
              ? { status: "renewed", token: current }
              : { status: "unavailable" };
          }
          // N8: a genuine credential rejection, where the token was not one
          // another tab had already replaced.
          reportRenewalFault("Session renewal rejected the shop's refresh token", {
            status,
          });
          clearAuthToken();
          return { status: "signed-out" };
        }
        // 429, 5xx, anything else: keep the tokens and try later. Deliberately
        // NOT reported per occurrence -- see reportRenewalFault.
        return { status: "unavailable" };
      }

      const data = (await response.json()) as { token?: string; refresh_token?: string };
      if (!data.token) {
        logger.warn("Token refresh response carried no access token");
        // N8: a 200 with no token is a contract break BY THE SERVICE, not a
        // customer condition.
        reportRenewalFault("Session renewal returned 200 with no access token");
        return { status: "unavailable" };
      }

      storeAuthTokens(data.token, data.refresh_token);
      logger.debug("Session renewed from the refresh token");
      // A5: dismiss the "we could not reach the sign-in service" banner as soon
      // as one renewal succeeds.
      announceRenewalRecovered();
      return { status: "renewed", token: data.token };
    } catch (error) {
      logger.error("Token refresh request failed:", error);
      /*
       * Correction 4 (item 4): a 200 whose body will not parse is a contract
       * break by the service and must reach monitoring; it used to fall in
       * here, log, and leave the customer unable to renew with nothing anywhere
       * saying why.
       *
       * The catch deliberately clears NOTHING. `status` is null when fetch()
       * itself throws -- an unreachable service or a dropped network -- and
       * shouldClearTokensForRefreshStatus(null) is already false, so a call
       * here would have been redundant; leaving it out makes the intent
       * unmistakable.
       */
      if (status === 200) {
        reportRenewalFault("Session renewal returned 200 with an unreadable body", {
          error: String(error),
        });
      }
      return { status: "unavailable" };
    }
  }

  /*
   * A10 (FCR S687): isTokenValid(), scheduleRefresh() and the CachedToken they
   * read were removed here, in their own change, because none of them could run
   * -- and this is not harmless dead code. It CAUSED item N7: the visibility
   * handler asked `if (this.isPageVisible && this.cachedToken)`, a condition
   * that could never be true, so the one mechanism meant to catch a missed
   * renewal could not fire, and a reader auditing that handler saw a plausible
   * guard and moved on.
   *
   * scheduleProactiveRefreshFromStorage() below is the live scheduler: it reads
   * the stored token's own `exp` claim, which is the authoritative expiry.
   */

  /**
   * Correction 4 (FCR S674): schedule the next proactive renewal from the
   * STORED token rather than from the in-memory cache.
   *
   * The renewal work stores its new access token in localStorage, so the
   * in-memory `cachedToken` -- which `scheduleRefresh` reads -- is empty on that
   * path and nothing was ever scheduled. This reads the stored token's own
   * `exp` claim, which is the authoritative expiry, and schedules one renewal a
   * minute before it.
   *
   * It cancels the previous timer first. Without that the page accumulates a
   * timer on every renewal, and an hour into a session there are as many
   * pending refreshes as there have been renewals.
   */
  scheduleProactiveRefreshFromStorage(): void {
    this.cancelScheduledRefresh();

    const token = getStoredAuthToken();
    if (!token) return;

    const payload = decodeJwtPayload(token);
    const exp = payload?.exp as number | undefined;
    if (!exp) return;

    const msUntilRefresh = exp * 1000 - Date.now() - this.TOKEN_REFRESH_BUFFER;
    if (msUntilRefresh <= 0) return;

    this.refreshTimer = setTimeout(() => {
      if (!this.isPageVisible) {
        /*
         * N7 (FCR S687): a missed proactive renewal is DEFERRED, not dropped.
         *
         * This used to be a bare `return` -- no else, no reschedule, no retry --
         * so a customer with the shop in a background tab was not renewed
         * proactively and was not renewed on returning either (the visibility
         * handler's guard could never fire; see A10). Their first action after
         * coming back got a 401, and under N1 that signed them out. The two
         * defects compounded: N7 made the 401 happen, N1 turned it into a
         * sign-out.
         *
         * Renewing a hidden tab is wasteful and browsers throttle the timer
         * anyway, so it is marked owed and the visibility handler picks it up.
         */
        this.renewalOwed = true;
        return;
      }
      logger.debug("Proactively renewing the session before it expires");
      this.refreshToken().catch((error) => {
        logger.error("Proactive token refresh failed:", error);
      });
    }, msUntilRefresh);
  }

  /**
   * Correction 4 (FCR S674): called whenever a new pair of tokens is stored.
   *
   * One place for "the tokens changed, act on it", so a caller that stores
   * tokens cannot forget to reschedule -- which is how the renewal stopped
   * happening after the first hour.
   */
  onTokensStored(): void {
    this.scheduleProactiveRefreshFromStorage();
  }

  /**
   * Requirement 7 (FCR S674): is a session renewal in flight?
   *
   * There is a moment where the access token has expired and the renewed one
   * has not arrived. Without this the route guards see "no session" and send a
   * signed-in customer to the sign-in page -- for an instant on every visit
   * after an hour away, which is exactly the flash the requirement is about.
   */
  isRenewalPending(): boolean {
    return this.renewalPending;
  }

  /**
   * Should a route guard hold rather than decide the customer is signed out?
   *
   * True while a renewal is in flight, and true once before any renewal has
   * been attempted when there is a refresh token to attempt it with. It stops
   * being true after that attempt, whatever the attempt returned -- so a
   * customer whose refresh token is no longer good reaches the sign-in page
   * rather than a spinner.
   */
  shouldHoldForRenewal(): boolean {
    if (this.renewalPending) return true;
    if (this.renewalAttempted) return false;
    return !!getStoredRefreshToken();
  }

  /** Start the one renewal attempt the hold above is waiting for. */
  beginRenewalIfNeeded(): void {
    if (this.renewalAttempted || this.renewalPending) return;
    if (!getStoredRefreshToken()) return;
    void this.refreshToken().catch(() => {
      // The failure shape is the refresh path's business; the guard only needs
      // the attempt to have happened.
    });
  }

  private setRenewalPending(pending: boolean): void {
    if (this.renewalPending === pending) return;
    this.renewalPending = pending;
    try {
      window.dispatchEvent(new Event(AUTH_RENEWAL_EVENT));
    } catch {
      // Nothing is listening outside a browser.
    }
  }

  /**
   * Cancel any scheduled refresh timer
   */
  private cancelScheduledRefresh(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  /**
   * Clear the token cache and cancel any scheduled refresh
   */
  clearCache(): void {
    this.cancelScheduledRefresh();
    this.renewalAttempted = false;
    this.renewalOwed = false;
    this.setRenewalPending(false);
    logger.debug("Token cache cleared");
  }

  /**
   * Get renewal statistics.
   *
   * A10 (S687): getTimeUntilExpiry() and hasToken() were removed with the cache
   * they read -- both were permanently null/false -- and `cacheHits` with them.
   * What remains is what the renewal path actually writes: refreshCount,
   * failedRefreshes and lastRefreshAt, the last of which the throttle depends on.
   */
  getStats(): Readonly<TokenCacheStats> {
    return { ...this.stats };
  }

  /**
   * Cleanup resources (call on app unmount)
   */
  destroy(): void {
    this.cancelScheduledRefresh();

    if (this.visibilityHandler && typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", this.visibilityHandler);
      this.visibilityHandler = null;
    }

    logger.debug("Token cache destroyed");
  }
}

// Export singleton instance
export const tokenCache = new TokenCacheService();

// Export function for backward compatibility
export const fetchSession = (): Promise<string | null> => {
  return tokenCache.getToken();
};

// Export type for testing
export { TokenCacheService };

// ============================================================================
// Issue 2 (FCR Pass 10): JWT localStorage helpers for Auth Service cutover.
// These are additive -- the Better Auth cookie flow is unchanged until Wave C.
// All helpers are no-ops when no JWT is present (pre-cutover safe).
// ============================================================================

const DS_AUTH_TOKEN_KEY = "ds_auth_token";

/**
 * Requirement 1 (FCR S674): the shop stores TWO tokens.
 *
 * The access token lives sixty minutes; the refresh token is the long-lived
 * credential that renews it. Sixty minutes after signing in -- or sixty minutes
 * after a purchase -- the customer used to be signed out and sent back to the
 * website, because the function that would renew the session returned null
 * without making any call and the 401 interceptors called it.
 */
const DS_REFRESH_TOKEN_KEY = "ds_refresh_token";

/**
 * Requirement 7 (FCR S674): fired when a session renewal starts or finishes, so
 * a route guard can hold instead of deciding the customer is signed out during
 * the moment between the two.
 */
export const AUTH_RENEWAL_EVENT = "ds-auth-renewal";

/**
 * TA-2 (S576): storeAuthToken used to change the session and tell nothing.
 * React components that had already decided they were logged out had no reason
 * to look again, so the header and the sidebar stayed in their logged-out state
 * until a full page reload -- which is what "signing in appears to do nothing"
 * actually was.
 *
 * Both store and clear dispatch this event. Dispatching on clear is what makes
 * signing out take effect immediately rather than at the next reload.
 */
export const AUTH_TOKEN_EVENT = "ds-auth-updated";

/**
 * A5 (FCR S687): the visible half of N1.
 *
 * After N1, a renewal that comes back `unavailable` no longer signs the customer
 * out -- the request is rejected and the session is kept. But nothing told the
 * customer anything, and on the dashboard that is a panel which fails to load
 * with no explanation, which reads as "the shop is broken" rather than "try
 * again in a moment".
 *
 * Two events rather than one flag, so the banner can be driven by whichever
 * handler saw the outcome without either of them owning the state.
 */
export const AUTH_RENEWAL_UNAVAILABLE_EVENT = "ds-auth-renewal-unavailable";
export const AUTH_RENEWAL_RECOVERED_EVENT = "ds-auth-renewal-recovered";

/** A renewal could not be completed, and the session has deliberately been kept. */
export function announceRenewalUnavailable(): void {
  try {
    window.dispatchEvent(new Event(AUTH_RENEWAL_UNAVAILABLE_EVENT));
  } catch {
    // Nothing is listening outside a browser.
  }
}

/** A renewal succeeded, so anything shown for the case above can go. */
export function announceRenewalRecovered(): void {
  try {
    window.dispatchEvent(new Event(AUTH_RENEWAL_RECOVERED_EVENT));
  } catch {
    // Nothing is listening outside a browser.
  }
}

function announceAuthChange(): void {
  try {
    window.dispatchEvent(new Event(AUTH_TOKEN_EVENT));
  } catch {
    // Ignore -- non-browser environments have nothing listening.
  }
}

/**
 * Decode the payload of a JWT token safely.
 * Handles base64url padding and UTF-8 encoding so accented names are not
 * garbled. Returns null on any parse error.
 */
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const seg = token.split(".")[1];
    if (!seg) return null;
    // Restore base64url padding
    let b64 = seg.replace(/-/g, "+").replace(/_/g, "/");
    while (b64.length % 4 !== 0) b64 += "=";
    const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    return null;
  }
}

/**
 * Read the stored JWT from localStorage only if it is not expired.
 * An expired token is treated as absent so the 401 interceptor redirects to
 * login rather than re-attaching a dead credential.
 */
export function getStoredAuthToken(): string | null {
  try {
    const token = localStorage.getItem(DS_AUTH_TOKEN_KEY);
    if (!token) return null;
    const payload = decodeJwtPayload(token);
    const exp = payload?.exp as number | undefined;
    if (exp && exp * 1000 <= Date.now()) {
      // Token expired -- remove it so the next getToken() triggers a fresh auth
      localStorage.removeItem(DS_AUTH_TOKEN_KEY);
      return null;
    }
    return token;
  } catch {
    return null;
  }
}

/** Persist a JWT to localStorage under the canonical key. */
export function storeAuthToken(token: string): void {
  storeAuthTokens(token);
}

/**
 * Remove BOTH tokens from localStorage (on logout or session expiry).
 *
 * Requirement 6 (FCR S674): a thirty-day refresh token that is only deleted
 * from the browser is still valid at the service. On a shared machine, removing
 * it from storage is the half that matters most, and this is that half.
 *
 * ITEM 8 (FCR S687) DELIVERED THE OTHER HALF, and this note used to say
 * something that is now wrong twice over. It said revoking at the service "is
 * the third pass-through endpoint -- Group 3 item 17, which waits on that
 * service confirming its routes". Both halves are false now:
 *
 *  - it is NOT waiting. `revokeStoredRefreshToken()` is in this file, the
 *    service's routes were read directly, and `authClient.signOut()` and
 *    `redirectToDeskshareLogout()` both call it BEFORE anything clears storage
 *    -- clearing first revokes nothing, and the ordering is the whole fix;
 *  - it is NOT a pass-through endpoint, and must never become one. The browser
 *    calls the service directly with the customer's own credential. A shop
 *    endpoint forwarding it would add a hop, add a place for the internal
 *    service key to leak into the wrong request, and give the service the
 *    SHOP's IP address rather than the customer's -- putting every customer
 *    into one bucket of 120 requests an hour (A4).
 *
 * `clearAuthToken()` itself deliberately does NOT revoke. It has eleven call
 * sites and several are failure paths -- `redirectToAuthLogin()` among them,
 * which runs when a token could not be RENEWED. Revoking there would destroy a
 * whole refresh family on the strength of a transient error, which is the
 * defect N1 is about. Revocation belongs to sign-out, and sign-out calls it
 * explicitly.
 *
 * The name is kept because those eleven call sites use it and every one of them
 * means "end this session in this browser".
 */
export function clearAuthToken(): void {
  try {
    localStorage.removeItem(DS_AUTH_TOKEN_KEY);
    localStorage.removeItem(DS_REFRESH_TOKEN_KEY);
  } catch {
    // Ignore
  }
  tokenCache.clearCache();
  announceAuthChange();
}

/**
 * What a signed-in visit leaves in this browser. Cleared on every sign-out so
 * the next person on a shared computer never sees the previous customer's cart.
 *
 * VM-3 (S787): kept here, beside the tokens -- the module that stores the
 * session also clears it -- and re-exported from auth-redirect.ts so every
 * existing import keeps working. Every sign-out (signOutEverywhere(), the cart's
 * own sign-out page) and a hand-off for a different person (below) use it.
 */
export const SIGNED_IN_STORAGE_KEYS = [
  "cart_price_id", // the chosen price, written by the cart's sign-in step
  "productIds", // the socket registration list written during checkout
  "machineId",
  "cartItems",
  "cartCoupon",
] as const;

/** Remove the previous customer's cart state. Survives blocked storage. */
export function clearSignedInState(): void {
  for (const key of SIGNED_IN_STORAGE_KEYS) {
    try {
      localStorage.removeItem(key);
    } catch {
      // Storage blocked: nothing was stored, so there is nothing to clear.
    }
  }
}

/**
 * Correction 2 (FCR S674): **do not sign the customer out on a server error.**
 *
 * The single most important behaviour in Part C. It was written as a function
 * rather than a line inside a handler because the handler did not exist yet --
 * and now it does: item 4 (FCR S687) made the renewal a real request, and this
 * rule is what `renewSession()` reads to decide between `signed-out` and
 * `unavailable`. There are no "pass-through endpoints" and there will not be;
 * the browser calls the service directly (A4).
 *
 * The rule mattering MORE now, not less, is the thing to keep in view: until
 * item 4 the renewal never succeeded and never failed, so nothing downstream of
 * this could be reached at all.
 *
 * The proposed handler cleared both tokens on any non-successful response.
 * `!response.ok` is true for 500, 502 and 503 as well as 401, so **a brief
 * outage would sign out every customer in the shop and destroy refresh tokens
 * that were still valid.** Only 400, 401 and 403 mean the refresh token is no
 * good. Everything else -- a server error, an unreachable service, a service
 * that is not configured, a rate limit -- must leave both tokens alone so the
 * next attempt can succeed.
 *
 * `null` is the shape used for "there was no response at all", which is the
 * unreachable case and the one the original proposal handled worst.
 */
export function shouldClearTokensForRefreshStatus(status: number | null): boolean {
  return status === 400 || status === 401 || status === 403;
}

/**
 * Requirement 3 (FCR S674): read the refresh token.
 *
 * Deliberately NOT expiry-checked the way the access token is. A refresh token
 * is opaque to the browser -- it is not required to be a JWT and its expiry is
 * the service's to enforce -- so treating an unreadable one as absent would
 * sign the customer out for a token that is perfectly good.
 */
export function getStoredRefreshToken(): string | null {
  try {
    return localStorage.getItem(DS_REFRESH_TOKEN_KEY) || null;
  } catch {
    return null;
  }
}

/**
 * Requirement 1 (FCR S674): store the pair, and reschedule the next renewal.
 *
 * One entry point, so a caller that stores tokens cannot forget to reschedule
 * -- which is correction 4's other half. The refresh token is optional: the
 * post-purchase path may deliver only an access token until the exchange route
 * exists, and an absent refresh token must not clear one the customer already
 * has.
 */
export function storeAuthTokens(accessToken: string, refreshToken?: string): void {
  try {
    localStorage.setItem(DS_AUTH_TOKEN_KEY, accessToken);
    if (refreshToken) {
      localStorage.setItem(DS_REFRESH_TOKEN_KEY, refreshToken);
    }
  } catch {
    // Ignore storage errors (private browsing, quota exceeded, etc.)
  }
  tokenCache.onTokensStored();
  announceAuthChange();
}

/**
 * Item 5 (FCR S687): exchange the OAuth sign-in handoff token for a full pair.
 *
 * The handoff token is the ONLY token the service will exchange:
 * TokenExchangeView refuses anything whose `purpose` claim is not `"handoff"`,
 * and only the OAuth handoff sets that claim. The post-purchase auto-login token
 * is built by `_build_token_pair()` with no purpose argument, so it has no
 * purpose claim and can NEVER be exchanged -- which is why items 1, 2 and 6 (the
 * checkout path) are the only way the shop obtains a refresh token after a
 * purchase, and why this is not a shortcut around them.
 *
 * It is single-use, enforced on the token's `jti` for 120 seconds, and it lives
 * about a minute.
 *
 * N3 (S687): no `credentials: "include"` here either -- same reason as the
 * refresh call.
 *
 * Returns true when a full pair was stored.
 */
export async function exchangeAuthToken(token: string, signal?: AbortSignal): Promise<boolean> {
  const baseUrl = authServiceBaseUrl();
  if (!baseUrl) {
    logger.warn("Auth Service URL not configured -- cannot exchange the sign-in token");
    return false;
  }

  try {
    const response = await fetch(`${baseUrl}/api/auth/token/exchange`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ token }),
      // No credentials. See N3.
      // SU-12 (FCR S732): the page is held until this answers, so it has a limit.
      signal,
    });

    if (!response.ok) {
      logger.warn("Token exchange failed", { status: response.status });
      /*
       * Correction 1 (item 5) -- THE dangerous line, deliberately absent.
       *
       * The proposed code called clearAuthTokens() here whenever
       * shouldClearTokensForRefreshStatus(status) was true. A failed exchange
       * answers 401 in every ORDINARY failure case: the handoff token has
       * expired (it lives about a minute), or it has already been spent. Both
       * happen to real customers -- a tab left open on the sign-in redirect, a
       * page restored by the browser, a customer who signs in on the website
       * while already signed in on the shop. Under that code a 401 from the
       * exchange destroyed the working session the customer already had.
       *
       * An exchange is an UPGRADE attempt. Failing to upgrade must never be
       * worse than not trying.
       */
      return false;
    }

    const data = (await response.json()) as { token?: string; refresh_token?: string };
    if (!data.token) {
      logger.warn("Token exchange response carried no access token");
      // N8: a 200 with no token is a contract break by the service.
      reportRenewalFault("Token exchange returned 200 with no access token");
      return false;
    }

    storeAuthTokens(data.token, data.refresh_token);
    logger.debug("Exchanged the sign-in handoff token for a full session");
    return true;
  } catch (error) {
    logger.error("Token exchange request failed:", error);
    return false;
  }
}

/**
 * Item 8 (FCR S687): revoke the refresh token at the service on sign-out.
 *
 * Clearing localStorage alone left a credential that stays valid on the server
 * for up to thirty days after the customer believes they have signed out. On a
 * shared or public machine that is the case that matters most, and it is one of
 * the two protections that make the thirty-day lifetime the right choice
 * (Decision 1) rather than the wrong one.
 *
 * Three things that are load-bearing here:
 *
 *  * N3 -- NO `credentials: "include"`, and here the reason is concrete rather
 *    than theoretical. `_read_refresh_token()` reads the cookie first on the
 *    revoke route as well, and the revoke revokes the whole FAMILY. With
 *    credentials sent, a customer signing out of the shop would revoke whatever
 *    family the `ds_refresh` cookie belongs to -- which may be their website
 *    sign-in. authClient.signOut() is documented in this codebase as the
 *    shop-only sign-out; sending credentials would silently make it a website
 *    sign-out too.
 *  * Read the refresh token BEFORE anything clears it. Clearing first and
 *    revoking second revokes nothing. That ordering is the whole fix.
 *  * The service always answers 200, whether or not the credential was valid --
 *    a caller signing out must never be told whether what it presented was still
 *    good -- so there is nothing to branch on and nothing to report.
 *
 * Never throws: sign-out must complete even if the request does not.
 */
export async function revokeStoredRefreshToken(): Promise<void> {
  const refresh = getStoredRefreshToken();
  if (!refresh) return;

  const baseUrl = authServiceBaseUrl();
  if (!baseUrl) return;

  try {
    await fetch(`${baseUrl}/api/auth/token/revoke`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ refresh_token: refresh }),
      // No credentials. See N3 -- this one would sign the customer out of the
      // Deskshare website as well.
    });
    logger.debug("Revoked the refresh token at the authentication service");
  } catch (error) {
    // A sign-out that cannot reach the service still clears this browser.
    logger.warn("Could not revoke the refresh token", { error: String(error) });
  }
}

/**
 * Read ?authToken= from the current URL, persist it, and exchange it for a full
 * token pair.
 *
 * Called once on app mount (root.tsx). Safe to call when no param is present.
 *
 * ORDERING (item 5): the address bar is cleaned FIRST, before the exchange is
 * attempted. If the exchange ran first and the page then navigated or reloaded,
 * the single-use handoff token would still be in the address bar and in the
 * browser's history. React StrictMode double-invokes effects in development and
 * both call sites are inside useEffect -- strip-first is what makes that safe,
 * because the first invocation removes `authToken` and the second returns early
 * without sending a second exchange. Reordering these two statements would
 * produce a 401 on every development sign-in.
 *
 * SU-12 (FCR S732): it RETURNS THE EXCHANGE, and the root layout waits for it.
 * The shop used to release the app in the same tick it started the exchange, so
 * every guard ran while the sign-in was still on the network and read "being
 * signed in" as "signed out": the cart sent a customer who had just signed in
 * straight back to sign in, and only the second attempt stuck (the first
 * exchange usually finished while the browser was leaving). The promise
 * resolves true when a full session was stored, false otherwise; at once when
 * there is no `authToken`. The exchange has its own limit
 * (AUTH_EXCHANGE_TIMEOUT_MS) so a hung call cannot hold the page.
 *
 * StrictMode: the second invocation finds the token already stripped and gets
 * the FIRST invocation's promise back, so the layout still waits for the one
 * exchange that was sent -- and still only one is sent.
 */
export const AUTH_EXCHANGE_TIMEOUT_MS = 10_000;

/**
 * Stable identity for comparing two tokens before a sign-in hand-off.
 *
 * VM-5 (S787): the same rule readSession() in auth-client.ts uses -- sub, then
 * user_id, then the address -- so the two cannot disagree about who a token is.
 */
function tokenIdentity(token: string): string | null {
  const payload = decodeJwtPayload(token);
  if (!payload) return null;
  const id = String(payload.sub ?? payload.user_id ?? "");
  if (id) return `id:${id}`;
  const email = String(payload.email ?? "").toLowerCase();
  return email ? `email:${email}` : null;
}

/**
 * Whose session does this browser hold? `undefined`: none at all. `null`: a
 * session is held but its owner cannot be read -- a refresh token with no
 * valid access token beside it (getStoredAuthToken() removes an expired access
 * token and leaves the refresh token, and the app reads it early, so an expired
 * one is normally gone before a hand-off arrives).
 */
function storedSessionIdentity(): string | null | undefined {
  const access = getStoredAuthToken();
  if (access) return tokenIdentity(access);
  return getStoredRefreshToken() ? null : undefined;
}

/**
 * VM-5 (S787): a hand-off for a different person ends the previous person's
 * session first -- revoke their refresh token at the service, clear both tokens
 * and clear their cart state -- whether or not the exchange then succeeds.
 *
 * "Keep the session already in place when the exchange fails" (below) was
 * written so a failed hand-off never signs a customer out. It did not ask whose
 * session it was, so on a shared computer the second person's failed hand-off
 * left the first person signed in.
 *
 * What must still not pass, and is kept: the SAME person's failed hand-off
 * keeps their session; a hand-off whose identity cannot be read changes
 * nothing (it is not evidence of anyone). Ended, beyond the proposed rule: a
 * held refresh token whose owner cannot be read -- its access token expired and
 * was removed -- because keeping it would let the second person's short session
 * renew as the first person. The price, in the safe direction: the same person
 * whose access token has expired AND whose hand-off then fails is asked to sign
 * in again later, rather than renewing quietly.
 */
async function endSessionIfHandoffIsSomeoneElse(
  handoffToken: string,
  signal?: AbortSignal,
): Promise<void> {
  const current = storedSessionIdentity();
  if (current === undefined) return;
  const incoming = tokenIdentity(handoffToken);
  if (!incoming || incoming === current) return;
  // The app is held until the hand-off settles (SU-12, layouts/root.tsx), and
  // the revoke has no limit of its own, so it shares the hand-off's: a revoke
  // that never answers must not hold the page. The request has already read the
  // refresh token, so clearing below cannot change what it revokes.
  await Promise.race([revokeStoredRefreshToken(), whenAborted(signal)]);
  clearAuthToken();
  clearSignedInState();
}

/** Resolves when the signal aborts (never, without one). */
function whenAborted(signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (!signal) return;
    if (signal.aborted) {
      resolve();
      return;
    }
    signal.addEventListener("abort", () => resolve(), { once: true });
  });
}

let pendingCapture: Promise<boolean> | null = null;

/** Test hook: forget an in-flight capture between tests. */
export function resetPendingAuthCaptureForTests(): void {
  pendingCapture = null;
}

export function captureAuthTokenFromUrl(): Promise<boolean> {
  try {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("authToken");
    if (!token) return pendingCapture ?? Promise.resolve(false);
    // Remove the token from the URL without adding a history entry -- FIRST.
    params.delete("authToken");
    const newSearch = params.toString();
    const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : "") + window.location.hash;
    window.history.replaceState(null, "", newUrl);
    logger.debug("Captured auth token from URL");

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), AUTH_EXCHANGE_TIMEOUT_MS);
    const capture = endSessionIfHandoffIsSomeoneElse(token, controller.signal)
      .then(() => exchangeAuthToken(token, controller.signal))
      .then((ok) => {
        clearTimeout(timer);
        if (ok) return true;
        /*
         * Correction 2 (item 5): a signed-in customer must not be DOWNGRADED.
         *
         * The proposed fallback stored the handoff token as the access token
         * whenever the exchange failed. A handoff token expires in seconds; if the
         * customer already holds a valid hour-long access token, overwriting it
         * with that is a downgrade. Store it only when there is no usable access
         * token already.
         */
        if (getStoredAuthToken()) {
          logger.debug("Exchange failed; keeping the session already in place");
          return false;
        }
        storeAuthToken(token);
        logger.debug(
          "Stored the sign-in handoff token without exchanging it -- this session is short and the customer will be asked to sign in again",
        );
        return false;
      });
    pendingCapture = capture;
    void capture.finally(() => {
      if (pendingCapture === capture) pendingCapture = null;
    });
    return capture;
  } catch {
    // Ignore -- non-critical path
    return Promise.resolve(false);
  }
}
